<template>
  <v-container fluid>
    <div class="dashboard-page">
      <v-row no-gutters class="d-flex justify-space-between mt-10 mb-6">
        <h1 class="page-title">Dashboard</h1>
      </v-row>

      <!-- Stats Cards -->
      <v-row>
        <v-col cols="12" sm="6" md="3">
          <v-card class="mx-1 mb-1 stat-card">
            <v-card-text class="pa-6">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="card-light-grey text-uppercase caption mb-1">Total Portfolio Items</div>
                  <span class="font-weight-bold card-dark-grey" style="font-size: 28px">
                    {{ stats.totalPortfolios }}
                  </span>
                </div>
                <v-avatar color="primary" size="48">
                  <v-icon dark>mdi-image-multiple</v-icon>
                </v-avatar>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="mx-1 mb-1 stat-card">
            <v-card-text class="pa-6">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="card-light-grey text-uppercase caption mb-1">Categories</div>
                  <span class="font-weight-bold card-dark-grey" style="font-size: 28px">
                    {{ stats.totalCategories }}
                  </span>
                </div>
                <v-avatar color="success" size="48">
                  <v-icon dark>mdi-folder-multiple</v-icon>
                </v-avatar>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="mx-1 mb-1 stat-card">
            <v-card-text class="pa-6">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="card-light-grey text-uppercase caption mb-1">Image Projects</div>
                  <span class="font-weight-bold card-dark-grey" style="font-size: 28px">
                    {{ stats.imageCount }}
                  </span>
                </div>
                <v-avatar color="info" size="48">
                  <v-icon dark>mdi-camera</v-icon>
                </v-avatar>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" sm="6" md="3">
          <v-card class="mx-1 mb-1 stat-card">
            <v-card-text class="pa-6">
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="card-light-grey text-uppercase caption mb-1">Video / Web Projects</div>
                  <span class="font-weight-bold card-dark-grey" style="font-size: 28px">
                    {{ stats.videoWebCount }}
                  </span>
                </div>
                <v-avatar color="warning" size="48">
                  <v-icon dark>mdi-video</v-icon>
                </v-avatar>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Categories Breakdown & Recent Items -->
      <v-row>
        <v-col cols="12" md="5">
          <v-card class="mx-1 mb-1">
            <v-card-title class="pa-6 pb-3">
              <p>Portfolio by Category</p>
            </v-card-title>
            <v-card-text class="pa-6 pt-0">
              <ApexChart
                v-if="categoryChartReady"
                height="280"
                type="donut"
                :options="categoryChart.options"
                :series="categoryChart.series"
              ></ApexChart>
              <div v-else class="d-flex justify-center align-center" style="height: 280px">
                <v-progress-circular indeterminate color="primary"></v-progress-circular>
              </div>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" md="7">
          <v-card class="mx-1 mb-1">
            <v-card-title class="pa-6 pb-3">
              <p>Recent Portfolio Items</p>
              <v-spacer></v-spacer>
              <v-btn
                small
                color="secondary"
                class="text-capitalize"
                @click="$router.push('/portfolio')"
              >View All</v-btn>
            </v-card-title>
            <v-card-text class="pa-6 pt-0">
              <v-row v-if="recentItems.length">
                <v-col
                  v-for="item in recentItems"
                  :key="item.id"
                  cols="6"
                  sm="4"
                  md="4"
                >
                  <v-card outlined class="recent-item-card">
                    <v-img
                      :src="storageUrl + item.image"
                      height="120"
                      class="grey lighten-3"
                    >
                      <template v-slot:placeholder>
                        <v-row class="fill-height ma-0" align="center" justify="center">
                          <v-progress-circular indeterminate color="grey lighten-2"></v-progress-circular>
                        </v-row>
                      </template>
                    </v-img>
                    <v-card-text class="pa-2">
                      <div class="caption text-truncate">{{ item.name || 'Untitled' }}</div>
                      <div class="caption card-light-grey">{{ getCategoryName(item.portfolio_category_id) }}</div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
              <div v-else class="d-flex justify-center align-center" style="height: 200px">
                <div class="text-center">
                  <v-icon large color="grey lighten-1">mdi-image-off</v-icon>
                  <div class="card-light-grey mt-2">No portfolio items yet</div>
                  <v-btn
                    small
                    color="primary"
                    class="mt-3 text-capitalize"
                    @click="$router.push('/add_portfolio')"
                  >Add Your First Item</v-btn>
                </div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Quick Actions -->
      <v-row>
        <v-col cols="12">
          <v-card class="mx-1 mb-1">
            <v-card-title class="pa-6 pb-3">
              <p>Quick Actions</p>
            </v-card-title>
            <v-card-text class="pa-6 pt-0">
              <v-btn
                color="primary"
                class="mr-3 mb-2 text-capitalize"
                @click="$router.push('/add_portfolio')"
              >
                <v-icon left>mdi-plus</v-icon>
                Add Portfolio Item
              </v-btn>
              <v-btn
                color="success"
                class="mr-3 mb-2 text-capitalize"
                @click="$router.push('/portfolio_categories')"
              >
                <v-icon left>mdi-folder-plus</v-icon>
                Manage Categories
              </v-btn>
              <v-btn
                color="secondary"
                class="mb-2 text-capitalize"
                @click="$router.push('/portfolio')"
              >
                <v-icon left>mdi-view-grid</v-icon>
                View Portfolio
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import axios from "axios";
import ApexChart from "vue-apexcharts";
import config from "@/config";

export default {
  name: "Dashboard",
  components: {
    ApexChart,
  },
  data() {
    return {
      storageUrl: process.env.VUE_APP_STORAGE_URL,
      categories: [],
      portfolios: [],
      categoryChartReady: false,
      stats: {
        totalPortfolios: 0,
        totalCategories: 0,
        imageCount: 0,
        videoWebCount: 0,
      },
      categoryChart: {
        options: {
          dataLabels: { enabled: true, formatter: (val) => Math.round(val) + "%" },
          colors: [config.light.primary, config.light.secondary, config.light.success, config.light.warning, config.light.info],
          labels: [],
          legend: { position: "bottom" },
        },
        series: [],
      },
    };
  },
  computed: {
    recentItems() {
      return this.portfolios.slice(0, 6);
    },
  },
  methods: {
    async fetchData() {
      try {
        const [catRes, portRes] = await Promise.all([
          axios.get("portfolio-categories"),
          axios.get("portfolios"),
        ]);
        this.categories = catRes.data || [];
        this.portfolios = portRes.data || [];
        this.computeStats();
        this.buildCategoryChart();
      } catch (e) {
        console.error("Failed to load dashboard data", e);
      }
    },
    computeStats() {
      this.stats.totalPortfolios = this.portfolios.length;
      this.stats.totalCategories = this.categories.length;

      const imageCatIds = this.categories.filter(c => c.type === 1).map(c => c.id);
      this.stats.imageCount = this.portfolios.filter(p => imageCatIds.includes(p.portfolio_category_id)).length;
      this.stats.videoWebCount = this.stats.totalPortfolios - this.stats.imageCount;
    },
    buildCategoryChart() {
      const countMap = {};
      this.categories.forEach(c => { countMap[c.id] = { name: c.name, count: 0 }; });
      this.portfolios.forEach(p => {
        if (countMap[p.portfolio_category_id]) {
          countMap[p.portfolio_category_id].count++;
        }
      });

      const entries = Object.values(countMap).filter(e => e.count > 0);
      if (entries.length) {
        this.categoryChart.options = {
          ...this.categoryChart.options,
          labels: entries.map(e => e.name),
        };
        this.categoryChart.series = entries.map(e => e.count);
      } else {
        this.categoryChart.options = {
          ...this.categoryChart.options,
          labels: this.categories.map(c => c.name),
        };
        this.categoryChart.series = this.categories.map(() => 0);
      }
      this.categoryChartReady = true;
    },
    getCategoryName(catId) {
      const cat = this.categories.find(c => c.id === catId);
      return cat ? cat.name : "Uncategorized";
    },
  },
  mounted() {
    this.fetchData();
  },
};
</script>

<style src="./Dashboard.scss" lang="scss"/>
